import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  Calendar,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

interface CrewMemberWithAssignments {
  id: string;
  name: string;
  email: string;
  event_date: string;
  assignment_count: number;
}

interface CalendarLog {
  crew_member_id: string;
  status: string;
  sent_at: string | null;
  error_message: string | null;
}

export function EmergencyModeSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const [selectedBriefingId, setSelectedBriefingId] = useState<string>("");
  const [editedEmails, setEditedEmails] = useState<Record<string, string>>({});
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());

  // Fetch user's briefings (productions)
  const { data: briefings, isLoading: briefingsLoading } = useQuery({
    queryKey: ["producer-briefings-emergency"],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("briefings")
        .select("id, festival_name, festival_date_start, festival_date_end, status")
        .eq("producer_id", userId)
        .eq("status", "published")
        .order("festival_date_start", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  // Auto-select first briefing
  useEffect(() => {
    if (briefings && briefings.length > 0 && !selectedBriefingId) {
      setSelectedBriefingId(briefings[0].id);
    }
  }, [briefings, selectedBriefingId]);

  // Fetch crew members with assignment counts for selected briefing
  const { data: crewMembers, isLoading: crewLoading } = useQuery({
    queryKey: ["crew-members-emergency", selectedBriefingId],
    queryFn: async () => {
      if (!selectedBriefingId) return [];

      // Get crew members
      const { data: crew, error: crewError } = await supabase
        .from("crew_members")
        .select("id, name, email, event_date")
        .eq("briefing_id", selectedBriefingId)
        .order("event_date", { ascending: true });

      if (crewError) throw crewError;

      // Get assignment counts
      const { data: assignments, error: assignmentError } = await supabase
        .from("crew_assignments")
        .select("crew_member_id")
        .eq("briefing_id", selectedBriefingId)
        .eq("is_active", true);

      if (assignmentError) throw assignmentError;

      // Count assignments per crew member
      const countMap: Record<string, number> = {};
      assignments?.forEach((a) => {
        if (a.crew_member_id) {
          countMap[a.crew_member_id] = (countMap[a.crew_member_id] || 0) + 1;
        }
      });

      return (crew || []).map((c) => ({
        ...c,
        assignment_count: countMap[c.id] || 0,
      })) as CrewMemberWithAssignments[];
    },
    enabled: !!selectedBriefingId,
  });

  // Fetch send logs for this briefing
  const { data: sendLogs, refetch: refetchLogs } = useQuery({
    queryKey: ["emergency-calendar-logs", selectedBriefingId],
    queryFn: async () => {
      if (!selectedBriefingId) return {} as Record<string, CalendarLog>;

      const { data, error } = await supabase
        .from("emergency_calendar_logs")
        .select("crew_member_id, status, sent_at, error_message")
        .eq("briefing_id", selectedBriefingId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get latest log per crew member
      const latestLogs: Record<string, CalendarLog> = {};
      data?.forEach((log) => {
        if (!latestLogs[log.crew_member_id]) {
          latestLogs[log.crew_member_id] = log;
        }
      });

      return latestLogs;
    },
    enabled: !!selectedBriefingId,
  });

  // Send calendar mutation
  const sendCalendarMutation = useMutation({
    mutationFn: async ({ crewMemberId, email }: { crewMemberId: string; email: string }) => {
      const { data, error } = await supabase.functions.invoke("send-emergency-calendar", {
        body: {
          crewMemberId,
          briefingId: selectedBriefingId,
          overrideEmail: email,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      refetchLogs();
      queryClient.invalidateQueries({ queryKey: ["emergency-calendar-logs"] });
    },
  });

  const handleSendCalendar = async (crewMemberId: string, email: string) => {
    setSendingIds((prev) => new Set(prev).add(crewMemberId));
    try {
      await sendCalendarMutation.mutateAsync({ crewMemberId, email });
      toast({
        title: "Calendar sent",
        description: `Calendar file sent to ${email}`,
      });
    } catch (error: unknown) {
      toast({
        title: "Failed to send calendar",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev);
        next.delete(crewMemberId);
        return next;
      });
    }
  };

  const handleSendAll = async () => {
    if (!crewMembers) return;

    const crewWithAssignments = crewMembers.filter((c) => c.assignment_count > 0);

    for (const crew of crewWithAssignments) {
      const email = editedEmails[crew.id] || crew.email;
      await handleSendCalendar(crew.id, email);
    }
  };

  const getEmailForCrew = (crew: CrewMemberWithAssignments) => {
    return editedEmails[crew.id] ?? crew.email;
  };

  const getStatusBadge = (crewId: string) => {
    const log = sendLogs?.[crewId];
    if (!log) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Not sent
        </Badge>
      );
    }
    if (log.status === "sent" && log.sent_at) {
      return (
        <Badge variant="outline" className="border-green-500/50 text-green-500">
          <CheckCircle className="mr-1 h-3 w-3" />
          Sent {format(new Date(log.sent_at), "HH:mm")}
        </Badge>
      );
    }
    if (log.status === "error") {
      return (
        <Badge variant="outline" className="border-destructive/50 text-destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Error
        </Badge>
      );
    }
    return <Badge variant="outline">Pending</Badge>;
  };

  const selectedBriefing = briefings?.find((b) => b.id === selectedBriefingId);

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="border-amber-500/30 bg-amber-500/5 p-6">
        <div className="flex gap-4">
          <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-500" />
          <div>
            <h3 className="mb-2 font-semibold text-amber-500">Emergency mode</h3>
            <p className="text-sm text-muted-foreground">
              If PROduce notifications or the app is unstable, you can send calendar files (.ics) to
              your crew members via email. They can import these into Google Calendar, Apple
              Calendar, or any other calendar app to see all their assignments.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              For questions, please mail to{" "}
              <a href="mailto:support@produce-app.com" className="text-primary hover:underline">
                support@produce-app.com
              </a>
            </p>
          </div>
        </div>
      </Card>

      {/* Production Selector */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Select production</h3>
          </div>

          {briefingsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading productions...
            </div>
          ) : briefings && briefings.length > 0 ? (
            <Select value={selectedBriefingId} onValueChange={setSelectedBriefingId}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a production" />
              </SelectTrigger>
              <SelectContent>
                {briefings.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.festival_name || "Unnamed production"}
                    {b.festival_date_start &&
                      ` (${format(new Date(b.festival_date_start), "dd-MM-yyyy")})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-muted-foreground">No published productions found.</p>
          )}
        </div>
      </Card>

      {/* Crew Table */}
      {selectedBriefingId && (
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Crew members</h3>
              {selectedBriefing && (
                <span className="text-sm text-muted-foreground">
                  - {selectedBriefing.festival_name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => refetchLogs()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh status
              </Button>
              <Button
                onClick={handleSendAll}
                disabled={
                  !crewMembers || crewMembers.filter((c) => c.assignment_count > 0).length === 0
                }
              >
                <Send className="mr-2 h-4 w-4" />
                Send calendars to all crew members
              </Button>
            </div>
          </div>

          {crewLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading crew members...
            </div>
          ) : crewMembers && crewMembers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Assignments</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {crewMembers.map((crew) => (
                  <TableRow key={crew.id}>
                    <TableCell className="font-medium">{crew.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(crew.event_date), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="email"
                        value={getEmailForCrew(crew)}
                        onChange={(e) =>
                          setEditedEmails((prev) => ({
                            ...prev,
                            [crew.id]: e.target.value,
                          }))
                        }
                        className="max-w-xs"
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{crew.assignment_count}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(crew.id)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendCalendar(crew.id, getEmailForCrew(crew))}
                        disabled={sendingIds.has(crew.id) || crew.assignment_count === 0}
                      >
                        {sendingIds.has(crew.id) ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        Send calendar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="py-8 text-center text-muted-foreground">
              No crew members found for this production.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
